/**
 * TasksContext — lightweight global store for "Mes tâches" badge + sync.
 *
 * iter284 Phase B — Refactored to share the SINGLE realtime Socket.IO
 * connection maintained by RealtimeContext. Previously this file opened
 * its own `io(API)` in parallel, which is why the network trace
 * showed 20× /api/socket.io/ per page. Now: 1 socket, N listeners.
 *
 * Responsibilities:
 *   - On login, fetch /api/tasks/my/count and keep it up to date
 *   - Listen to the shared realtime socket for task-triggering events
 *   - Expose { pending, done, total, refresh }
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useRealtime } from '@/context/RealtimeContext';

const API = process.env.REACT_APP_BACKEND_URL;
const Ctx = createContext({ pending: 0, done: 0, total: 0, refresh: () => {} });

export function TasksProvider({ children }) {
  const { user } = useAuth();
  const { socket } = useRealtime();  // shared connection — no new io() here.
  const [state, setState] = useState({ pending: 0, done: 0, total: 0 });
  const timerRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await axios.get(`${API}/api/tasks/my/count`,
        { withCredentials: true });
      setState({
        pending: data.pending || 0,
        done: data.done || 0,
        total: data.total || 0,
      });
    } catch { /* silent */ }
  }, [user]);

  // Initial fetch + safety poll every 5 min (only when tab is visible —
  // Page Visibility guard added iter284 Phase B).
  useEffect(() => {
    if (!user) return;
    refresh();
    let stopped = false;
    const tick = () => {
      if (document.visibilityState === 'visible' && !stopped) refresh();
    };
    timerRef.current = setInterval(tick, 5 * 60 * 1000);
    const onVis = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stopped = true;
      if (timerRef.current) clearInterval(timerRef.current);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, refresh]);

  // Debounced realtime refresh on relevant socket events — reuses the
  // RealtimeContext socket instead of opening a second connection.
  useEffect(() => {
    if (!user || !socket) return;
    let pending = null;
    const kick = () => {
      if (pending) clearTimeout(pending);
      pending = setTimeout(refresh, 400);
    };
    const onMsgUpd = () => kick();
    const onNewMsg = (msg) => {
      if (msg?.message_type === 'call_summary') kick();
    };
    const onNotif = (n) => {
      if (n?.type === 'call_task_assigned' || n?.type === 'call_task_reassigned') kick();
    };
    socket.on('message_updated', onMsgUpd);
    socket.on('new_message', onNewMsg);
    socket.on('notification', onNotif);
    return () => {
      try { socket.off('message_updated', onMsgUpd); } catch { /* no-op */ }
      try { socket.off('new_message', onNewMsg); } catch { /* no-op */ }
      try { socket.off('notification', onNotif); } catch { /* no-op */ }
      if (pending) clearTimeout(pending);
    };
  }, [user, socket, refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useTasks = () => useContext(Ctx);
